import type { Nullish } from '@aigc/types';
import type { ComplexPromptFormValues, MultiPromptFormValues } from './schema';

// ─── Public Types ────────────────────────────────────────────────────────────

export interface ParsedMultiPrompt {
  multi_prompt: MultiPromptFormValues;
  extra_prompt: string | null | undefined;
}

export class ParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ParseError';
  }
}

export type TemplateType =
  'excel' | 'standard' | 'duration-separated' | 'fallback';

// ─── Known Field Names (standard 解析器) ─────────────────────────────────────

/**
 * 规范字段名 → 目标字段的映射
 * 含别名（模版三/四的非标准字段名）
 */
const FIELD_ALIAS: Record<string, string> = {
  镜号: '镜号',
  时长: '时长',
  附长: '时长', // 模版三笔误
  镜头目的: '镜头目的',
  镜头: '镜头',
  运镜: '运镜',
  '镜头语言/运镜': '运镜', // 模版三
  画面描述: '画面描述',
  动作与动态: '画面描述', // 模版三
  '台词/字幕/旁白/情绪': '台词/字幕/旁白/情绪',
  '台词/字幕/情绪': '台词/字幕/旁白/情绪',
  画面关键词: '画面关键词',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * 从 "4秒" / "2.5秒" / "2.5" 中提取数字
 */
function parseDuration(value: string): number | null {
  const match = value.trim().match(/^(\d+(?:\.\d+)?)/);
  if (!match || !match[1]) return null;
  return Number.parseFloat(match[1]);
}

/**
 * 解析 Excel 时长区间 "0-3" → 3（终止 - 起始）
 * 若不含 `-` 则直接用 parseDuration
 */
function parseExcelDuration(value: string): number | null {
  const trimmed = value.trim();
  const rangeMatch = trimmed.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/);
  if (rangeMatch && rangeMatch[1] != null && rangeMatch[2] != null) {
    return Number.parseFloat(rangeMatch[2]) - Number.parseFloat(rangeMatch[1]);
  }
  return parseDuration(trimmed);
}

/**
 * 按 `、` `，` `,` `\t` 分割关键词，过滤空项
 */
function parseVisualKeywords(value: string): string[] {
  return value
    .split(/[、，,\t]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * 构建空白 ComplexPromptFormValues
 */
function emptyShot(): ComplexPromptFormValues {
  return {
    duration: 0,
    visual_description: '',
    shot_purpose: null,
    shot_type: null,
    camera_movement: null,
    dialogue_subtitle_emotion: null,
    visual_keywords: null,
  };
}

// ─── Standard Line Parser ─────────────────────────────────────────────────────

/** 匹配 [字段名]：值，兼容全角冒号 */
const FIELD_LINE_RE = /^\[([^\]]+)\][:：](.*)/s;

interface RawFields {
  shot_index: number | null;
  duration: number | null;
  shot_purpose: string[];
  shot_type: string[];
  camera_movement: string[];
  visual_description: string[];
  dialogue_subtitle_emotion: string[];
  visual_keywords: string[] | null;
  /** 未知字段源行 和 自由文本行，换行追加到 visual_description */
  extra_lines: string[];
}

function parseShot(
  lines: string[],
  usedShotIndices: Set<number>,
): ComplexPromptFormValues {
  const raw: RawFields = {
    shot_index: null,
    duration: null,
    shot_purpose: [],
    shot_type: [],
    camera_movement: [],
    visual_description: [],
    dialogue_subtitle_emotion: [],
    visual_keywords: null,
    extra_lines: [],
  };

  for (const line of lines) {
    const m = FIELD_LINE_RE.exec(line);
    if (!m) {
      // 非 [field]: 格式的自由文本行 → 追加到 visual_description
      const trimmed = line.trim();
      if (trimmed) raw.extra_lines.push(trimmed);
      continue;
    }

    const rawFieldName = m[1]?.trim() ?? '';
    const rawValue = m[2]?.trim() ?? '';
    const field = FIELD_ALIAS[rawFieldName];

    if (!field) {
      // 未知字段：保留源格式（原始行）追加到 visual_description
      raw.extra_lines.push(line);
      continue;
    }

    switch (field) {
      case '镜号': {
        const idx = Number.parseInt(rawValue, 10);
        if (Number.isNaN(idx)) {
          throw new ParseError(`镜号 "${rawValue}" 不是有效整数`);
        }
        if (usedShotIndices.has(idx)) {
          throw new ParseError(`镜号 ${idx} 重复`);
        }
        usedShotIndices.add(idx);
        raw.shot_index = idx;
        break;
      }
      case '时长':
        // 多次出现取最后一次
        raw.duration = parseDuration(rawValue);
        break;
      case '镜头目的':
        raw.shot_purpose.push(rawValue);
        break;
      case '镜头':
        raw.shot_type.push(rawValue);
        break;
      case '运镜':
        raw.camera_movement.push(rawValue);
        break;
      case '画面描述':
        raw.visual_description.push(rawValue);
        break;
      case '台词/字幕/旁白/情绪':
        raw.dialogue_subtitle_emotion.push(rawValue);
        break;
      case '画面关键词':
        raw.visual_keywords = parseVisualKeywords(rawValue);
        break;
    }
  }

  // 合并 visual_description：已知字段空格合并，多余行换行追加
  const knownDesc = raw.visual_description.join(' ');
  const extraPart = raw.extra_lines.join('\n');
  const visual_description = [knownDesc, extraPart]
    .filter((s) => s.length > 0)
    .join('\n');

  return {
    duration: raw.duration ?? 0,
    visual_description,
    shot_purpose: raw.shot_purpose.join(' ') || null,
    shot_type: raw.shot_type.join(' ') || null,
    camera_movement: raw.camera_movement.join(' ') || null,
    dialogue_subtitle_emotion: raw.dialogue_subtitle_emotion.join(' ') || null,
    visual_keywords: raw.visual_keywords,
  };
}

// ─── Template Detection ───────────────────────────────────────────────────────

/**
 * 根据文本关键特征识别模版类型
 */
export function detectTemplate(text: string): TemplateType {
  const firstNonEmpty = text.split('\n').find((l) => l.trim().length > 0) ?? '';

  // 模版一：第一个非空行含 \t，不以 [ 或 ## 开头
  if (
    firstNonEmpty.includes('\t') &&
    !firstNonEmpty.trimStart().startsWith('[') &&
    !firstNonEmpty.trimStart().startsWith('#')
  ) {
    return 'excel';
  }

  // 模版三/四/五（standard）：含 [镜号]:
  if (/\[镜号\][:：]/m.test(text)) {
    return 'standard';
  }

  // 模版二：含 [时长]: 但无 [镜号]:
  if (/\[时长\][:：]/m.test(text)) {
    return 'duration-separated';
  }

  return 'fallback';
}

// ─── Standard Template Parser ─────────────────────────────────────────────────

function parseStandardTemplate(
  scriptPart: string,
  extra_prompt: string | null,
): ParsedMultiPrompt {
  const allLines = scriptPart.split('\n');
  const headerLines: string[] = [];
  const rawSegments: string[][] = [];

  for (const line of allLines) {
    if (/^\[镜号\][:：]/.test(line)) {
      rawSegments.push([line]);
    } else if (rawSegments.length > 0) {
      rawSegments[rawSegments.length - 1]!.push(line);
    } else {
      headerLines.push(line);
    }
  }

  // 如果没有任何镜号标志，将全部内容作为第一个分镜的画面描述
  if (rawSegments.length === 0) {
    const visual_description = scriptPart
      .replace(/^##\s*镜头脚本\s*\n?/, '')
      .trim();
    return {
      multi_prompt: visual_description
        ? [{ ...emptyShot(), visual_description }]
        : [],
      extra_prompt,
    };
  }

  // 校验 header
  const headerContent = headerLines.join('\n').trim();
  if (headerContent !== '' && !/^##\s*镜头脚本$/.test(headerContent)) {
    throw new ParseError(
      `[镜号] 之前包含额外内容，期望仅有 "## 镜头脚本" 或空白，实际为：\n${headerContent}`,
    );
  }

  const usedShotIndices = new Set<number>();
  const segments = rawSegments.map((lines) =>
    parseShot(lines, usedShotIndices),
  );

  return { multi_prompt: segments, extra_prompt };
}

// ─── Duration-Separated Template Parser (模版二) ──────────────────────────────

/** 匹配 `旁白：` 或 `旁白:` 行首 */
const NARRATION_LINE_RE = /^旁白[:：](.*)$/s;
/** 匹配 `背景音乐` 相关行 */
const BGM_LINE_RE = /^背景音乐/;
/** 匹配 `[画面描述]：` */
const VISUAL_DESC_FIELD_RE = /^\[画面描述\][:：](.*)/s;

function parseDurationSeparated(
  scriptPart: string,
  extra_prompt: string | null,
): ParsedMultiPrompt {
  // 按 [时长]: 行作为分镜起始标志分组
  const allLines = scriptPart.replace(/^##\s*镜头脚本\s*\n?/, '').split('\n');

  const rawSegments: string[][] = [];

  for (const line of allLines) {
    if (/^\[时长\][:：]/.test(line)) {
      rawSegments.push([line]);
    } else if (rawSegments.length > 0) {
      rawSegments[rawSegments.length - 1]!.push(line);
    }
    // [时长] 之前的行忽略（通常是 ##镜头脚本 标题）
  }

  const segments = rawSegments.map((lines): ComplexPromptFormValues => {
    const shot = emptyShot();
    const descParts: string[] = [];

    for (const line of lines) {
      // [时长]：N秒
      const durationMatch = /^\[时长\][:：](.*)/.exec(line);
      if (durationMatch) {
        shot.duration = parseDuration(durationMatch[1]?.trim() ?? '') ?? 0;
        continue;
      }

      // [画面描述]：xxx → 剥离标签，值作为 visual_description 起始
      const descFieldMatch = VISUAL_DESC_FIELD_RE.exec(line);
      if (descFieldMatch) {
        const val = descFieldMatch[1]?.trim() ?? '';
        if (val) descParts.push(val);
        continue;
      }

      // 旁白：xxx
      const narrationMatch = NARRATION_LINE_RE.exec(line);
      if (narrationMatch) {
        const val = narrationMatch[1]?.trim() ?? '';
        if (shot.dialogue_subtitle_emotion) {
          shot.dialogue_subtitle_emotion += ' ' + val;
        } else {
          shot.dialogue_subtitle_emotion = val || null;
        }
        continue;
      }

      // 其余行（含 背景音乐 + 音效、场景、动作、关键细节 等）→ 追加 visual_description
      const trimmed = line.trim();
      if (trimmed) descParts.push(trimmed);
    }

    shot.visual_description = descParts.join('\n');
    return shot;
  });

  return { multi_prompt: segments, extra_prompt };
}

// ─── Excel TSV Template Parser (模版一) ──────────────────────────────────────

/**
 * 解析 Excel 复制格式的 TSV 行，处理 " 包裹和 "" 转义
 * 注意：多行单元格在 Excel 复制后每行仍以 \n 分隔，整个文件是连续的
 * 本函数处理单个逻辑行（已跨行合并后的）
 */
export function parseTsvRow(line: string): string[] {
  const cells: string[] = [];
  let i = 0;
  const len = line.length;

  while (i <= len) {
    if (i === len) {
      cells.push('');
      break;
    }
    if (line[i] === '"') {
      // 引号包裹的单元格
      let cell = '';
      i++; // 跳过开头引号
      while (i < len) {
        if (line[i] === '"') {
          if (line[i + 1] === '"') {
            // "" → "
            cell += '"';
            i += 2;
          } else {
            // 结束引号
            i++;
            break;
          }
        } else {
          cell += line[i];
          i++;
        }
      }
      cells.push(cell);
      // 跳过 \t 或到达行尾
      if (line[i] === '\t') i++;
    } else {
      // 非引号单元格，读到下一个 \t 或行尾
      const end = line.indexOf('\t', i);
      if (end === -1) {
        cells.push(line.slice(i));
        break;
      } else {
        cells.push(line.slice(i, end));
        i = end + 1;
      }
    }
  }

  return cells;
}

/**
 * 将 Excel 列名归一化（trim）后映射到目标字段
 */
const EXCEL_COL_MAP: Record<string, string> = {
  镜号: 'shot_index',
  时长: 'duration',
  场景描述: 'visual_description',
  画面动作与运镜: 'action_and_movement',
  旁白: 'dialogue_subtitle_emotion',
  BGM: 'bgm',
};

/**
 * 解析「画面动作与运镜」列内容：
 * 按 `[xxx]` 子行分割，`[运镜]` 子行 → camera_movement，其余 → visual_description
 */
function parseActionMovementCell(value: string): {
  camera_movement: string | null;
  visual_description_parts: string[];
} {
  const lines = value.split('\n');
  const cameraLines: string[] = [];
  const descLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    // 匹配 [运镜] 前缀
    if (/^\[运镜\]/.test(trimmed)) {
      // 去掉 [运镜] 前缀后取值
      cameraLines.push(trimmed.replace(/^\[运镜\]\s*/, ''));
    } else {
      descLines.push(trimmed);
    }
  }

  return {
    camera_movement: cameraLines.join(' ') || null,
    visual_description_parts: descLines,
  };
}

function parseExcelTemplate(
  text: string,
  extra_prompt: string | null,
): ParsedMultiPrompt {
  // Excel 多行单元格用换行分隔，需要先重新组合逻辑行（按 TSV 规则）
  // 策略：逐字符扫描，追踪是否在引号包裹内
  const logicalLines: string[] = [];
  let current = '';
  let inQuote = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (ch === '"') {
      if (inQuote && text[i + 1] === '"') {
        current += '""';
        i++;
      } else {
        inQuote = !inQuote;
        current += ch;
      }
    } else if (ch === '\n' && !inQuote) {
      logicalLines.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  if (current) logicalLines.push(current);

  if (logicalLines.length < 2) {
    return { multi_prompt: [], extra_prompt };
  }

  // 第一行为 header
  const headerRow = parseTsvRow(logicalLines[0]!);
  const colNames = headerRow.map((c) => c.trim());

  const segments: ComplexPromptFormValues[] = [];
  const usedShotIndices = new Set<number>();

  for (let r = 1; r < logicalLines.length; r++) {
    const row = parseTsvRow(logicalLines[r]!);
    if (row.every((c) => !c.trim())) continue; // 跳过空行

    const shot = emptyShot();
    const descParts: string[] = [];

    for (let c = 0; c < colNames.length; c++) {
      const colName = colNames[c] ?? '';
      const cellValue = row[c]?.trim() ?? '';
      const targetField = EXCEL_COL_MAP[colName];

      if (!targetField) {
        // 未知列：以「列名: 值」追加到 visual_description
        if (cellValue) descParts.push(`${colName}: ${cellValue}`);
        continue;
      }

      switch (targetField) {
        case 'shot_index': {
          // S1 → 1
          const numMatch = cellValue.match(/(\d+)$/);
          const idx = numMatch ? Number.parseInt(numMatch[1]!, 10) : NaN;
          if (!Number.isNaN(idx)) {
            if (usedShotIndices.has(idx)) {
              throw new ParseError(`镜号 ${idx} 重复`);
            }
            usedShotIndices.add(idx);
          }
          break;
        }
        case 'duration':
          shot.duration = parseExcelDuration(cellValue) ?? 0;
          break;
        case 'visual_description':
          if (cellValue) descParts.push(cellValue);
          break;
        case 'action_and_movement': {
          const { camera_movement, visual_description_parts } =
            parseActionMovementCell(cellValue);
          shot.camera_movement = camera_movement;
          descParts.push(...visual_description_parts);
          break;
        }
        case 'dialogue_subtitle_emotion':
          shot.dialogue_subtitle_emotion = cellValue || null;
          break;
        case 'bgm':
          if (cellValue) descParts.push(`BGM: ${cellValue}`);
          break;
      }
    }

    shot.visual_description = descParts.join('\n');
    segments.push(shot);
  }

  return { multi_prompt: segments, extra_prompt };
}

// ─── Main ─────────────────────────────────────────────────────────────────────

/**
 * 将镜头脚本文本解析为结构化对象，自动检测模版类型。
 *
 * @throws {ParseError} 文本格式不合法时抛出
 */
export function parsePrompt(text: Nullish<string>): ParsedMultiPrompt {
  if (!text) {
    return {
      extra_prompt: '',
      multi_prompt: [],
    };
  }

  // Step A: 提取 ##要点 / ## 要点 之后的内容（兼容有无空格）
  const extraSplit = text.split(/^##\s*要点\s*$/m);
  const scriptPart = extraSplit[0]!;
  const extraRaw =
    extraSplit.length > 1 ? extraSplit.slice(1).join('## 要点\n') : '';
  const extra_prompt = extraRaw.trim() || null;

  // Step B: 根据模版类型分发
  const templateType = detectTemplate(scriptPart);

  switch (templateType) {
    case 'excel':
      return parseExcelTemplate(scriptPart, extra_prompt);
    case 'duration-separated':
      return parseDurationSeparated(scriptPart, extra_prompt);
    default:
      return parseStandardTemplate(scriptPart, extra_prompt);
  }
}
